/**
 * Types du moteur. Aucune valeur numérique ici : tout vient de `content/*.json`
 * et `data/processed/*.json` (CLAUDE.md règle 1). Les schémas zod correspondants
 * vivent dans `scripts/src/schemas/` et sont exportés en JSON Schema dans `content/schema/`.
 */

/** Référence de source obligatoire pour tout chiffre (CLAUDE.md règle 2). */
export interface Source {
  /** Identifiant court, repris dans docs/sources.md (ex. "insee-cn-2025-t2"). */
  id: string;
  /** Titre du document. */
  title: string;
  /** Éditeur (INSEE, AFT, PLF 2026…). */
  publisher: string;
  url: string;
  /** Tableau, page ou onglet précis. */
  locator?: string;
  /** Date de consultation ISO (AAAA-MM-JJ). */
  accessed: string;
  /** Ce qu'on en tire, en une phrase. */
  note?: string;
}

/** Un chiffre sourcé. `low`/`high` obligatoires quand le paramètre est contesté. */
export interface Figure {
  /** null uniquement si `source === "TODO"` (chiffre non relevé) ; le moteur doit refuser une baseline avec des null. */
  value: number | null;
  unit: Unit;
  source: Source | "TODO";
  low?: number;
  high?: number;
  /** Année de référence du chiffre (si différente de l'année de base). */
  year?: number;
  note?: string;
  /** v0.3 (règle 2) : sources supplémentaires, en plus de `source`. */
  sources?: Source[];
  /**
   * v0.3 : statut de sourçage. `convention_de_jeu` = paramètre de jeu non sourcé, présenté comme tel (écran Hypothèses,
   * ADR 0014 §4) ; aucun chiffre sourcé ne porte ce statut.
   */
  status?: FigureStatus;
}

export type FigureStatus = "verifie_a_faire" | "une_source" | "conteste" | "TODO" | "convention_de_jeu";

export type Unit =
  | "bn_eur" // Md€ courants
  | "pct_gdp" // % du PIB
  | "pct" // pourcentage simple (taux, croissance…)
  | "pt" // points (de PIB, de pourcentage)
  | "years"
  | "mt_co2e" // millions de tonnes CO2e
  | "persons"
  | "index" // indice sans dimension (1 = besoin couvert)
  | "ratio" // coefficient sans dimension (multiplicateur, élasticité…)
  | "hours" // indicateurs tangibles de service (ADR 0005)
  | "months";

/** Paramètre du modèle (content/params.json). */
export interface Param extends Figure {
  id: string;
  label: string;
  /** Mission LOLF de rattachement (indicateurs tangibles de service `svcind_<clé>_base`, ADR 0005). */
  mission?: string;
  /** `svcind_<clé>_capacity_base` : la capacité suit les crédits de la mission (personnel) ou non (places, ADR 0016). */
  capacity_from_credits?: boolean;
}

export type ParamSet = Record<string, Param>;

/** Mission LOLF du budget général (crédits de paiement). */
export interface Mission {
  /** Code court stable (ex. "enseignement_scolaire"). */
  id: string;
  /** Intitulé officiel LOLF. */
  label: string;
  credits: Figure;
  /** Indice de service public initial (1 = besoin couvert), composite dépense/besoin. */
  service_index?: Figure;
}

export interface RevenueLine {
  id: string;
  label: string;
  amount: Figure;
  /** Élasticité au PIB nominal (si connue). */
  elasticity?: Figure;
}

export interface SectorBlock {
  balance: Figure;
  /** Trajectoire tendancielle du solde, année par année à partir de l'année de base + 1. */
  trend: Figure[];
  /** v0.3 (ADR 0010 §3) : trajectoire sourcée (année 1 du mandat = 2027), consommée en priorité (ADR 0014 §3). */
  trend_v03?: Figure[];
}

/**
 * Trajectoire de référence (PLF / PSMT) année par année, alignée sur `years` (contrat du constructeur de données v0.3).
 * Toute série est optionnelle ; une valeur `null` (source TODO) est remplacée par la dernière valeur connue (convention moteur).
 */
export interface Outlook {
  years: number[];
  inflation_pct?: (Figure | null)[];
  oat10_pct?: (Figure | null)[];
  nominal_gdp_growth_pct?: (Figure | null)[];
  real_gdp_growth_pct?: (Figure | null)[];
  apu_deficit_pct_gdp?: (Figure | null)[];
  apu_debt_pct_gdp?: (Figure | null)[];
  apu_interest_bn?: (Figure | null)[];
  /** Repli si `apu_interest_bn` manque (Rapport sur la dette, tableau 2). */
  apu_interest_pct_gdp?: (Figure | null)[];
  /** v0.3 (ADR 0010) : croissance potentielle et soldes ASSO / APUL de la trajectoire officielle (% du PIB). */
  potential_growth_pct?: (Figure | null)[];
  asso_balance_pct_gdp?: (Figure | null)[];
  apul_balance_pct_gdp?: (Figure | null)[];
  source?: Source | "TODO" | Source[];
  note?: string;
}

export interface Baseline {
  /** Trajectoire de référence (ADR 0009 §6) ; absente → comportement v0.2 (macro figée sur l'année de base). */
  outlook?: Outlook;
  meta: {
    base_year: number;
    /** Ex. "LFI 2026" ou "LFI 2025 + exécution 2024". */
    vintage: string;
    built_at: string;
    sources: Source[];
  };
  macro: Record<string, Figure>;
  state: {
    missions: Mission[];
    revenues: RevenueLine[];
    debt_interest: Figure;
    balance: Figure;
    /** v0.3 (ADR 0010 §2) : 31 missions LOLF officielles du budget général (LFI 2026 hors CAS Pensions). */
    lolf_missions?: Mission[];
    /** v0.3 : les 9 groupes du jeu, crédits = Σ des missions LOLF du groupe (`mission_groups.map`). Consommé en priorité. */
    missions_v03?: Mission[];
    /** v0.3 : recettes nettes de l'État 2026 (Σ = 318,7 Md€, prélèvements sur recettes compris). Consommé en priorité. */
    revenues_v03?: RevenueLine[];
    balance_budgetary_2026?: Figure;
  };
  /** v0.3 : mission LOLF → groupe de service du jeu (économies ciblées et indices de service agrégés par groupe). */
  mission_groups?: { note?: string; map: Record<string, string> };
  /** v0.3 : valeurs de départ mesurées des indicateurs tangibles de service (définitions officielles). */
  services?: Record<string, Figure & { label: string; mission?: string }>;
  asso: SectorBlock;
  apul: SectorBlock;
  apu: Record<string, Figure>;
  politics: Record<string, Figure>;
  trade: Record<string, Figure>;
}

/** Profil daté : scalaire (constant) ou tableau année par année (index 0 = année 1 du mandat). */
export type Profile = number | number[];

/**
 * `estimé_ia` : mesure dictée par le joueur, chiffrée par LLM sans source (aperçu provisoire, PRD §12) — hors catalogue sourcé.
 * `estimé_ia_source` / `estimé_ia_une_source` : chiffrage par recherche documentaire (PRD §15, ADR 0013) avec au moins deux
 * sources indépendantes / une seule source ; toujours hors catalogue officiel tant que le fact-check et Ben ne l'ont pas promue.
 */
export type MeasureStatus = "sourced" | "non_chiffré" | "estimé_ia" | "estimé_ia_source" | "estimé_ia_une_source";

export type MeasureCategory =
  | "retraites"
  | "sante"
  | "ecole"
  | "securite"
  | "immigration"
  | "fiscalite_menages"
  | "fiscalite_entreprises"
  | "energie"
  | "logement"
  | "travail"
  | "defense"
  | "etat"
  | "europe_dette";

/** Effets d'une mesure sur les canaux (PRD §8). Tous optionnels, tous des profils. */
export interface MeasureEffects {
  g?: Profile; // croissance (pt)
  u?: Profile; // chômage (pt)
  pov?: Profile; // pauvreté (pt)
  coh?: Profile; // cohésion (index)
  conf?: Profile; // confiance des marchés (index)
  eu?: Profile; // relation UE (index)
  co2?: Profile; // émissions : variation annuelle en % de la trajectoire tendancielle, cumulative (ADR 0004)
  lab?: Profile; // population active (pt)
  trade?: Profile; // balance commerciale (Md€, écart au tendanciel)
  asso?: Profile; // solde Sécu (Md€)
  apul?: Profile; // solde collectivités (Md€)
  svc?: Record<string, Profile>; // indices de service par mission (niveau, Δ vs 1)
  /** Unités de capacité ajoutées par indicateur tangible (`svcind_<clé>_capacity_base`, ADR 0016), quantités de la décomposition. */
  capacity?: Record<string, Profile>;
  /**
   * Variation du stock de dette en % du stock (négatif = annulation), appliquée une fois par valeur de profil
   * (scalaire → année 1 seulement). Une annulation est traitée comme portant sur des titres détenus par
   * l'Eurosystème : le dividende Banque de France correspondant (stock annulé × taux apparent) disparaît.
   */
  debt_share?: Profile;
  /** Choc d'inflation (pt), avec persistance paramétrée (chocs matières premières…). */
  infl?: Profile;
  /** Choc sur l'OAT 10 ans (pt), avec persistance paramétrée (chocs BCE, PRD §3.6). */
  oat?: Profile;
  /** Majorité au Parlement (points de soutien, 0–100) : mesure → à l'adoption (scalaire) ou profil ; événement → une fois. ADR 0005. */
  maj?: Profile;
  /** Pouvoir d'achat : effet direct sur le revenu disponible (% du RDB, niveau après montée en charge), ex. hausse du SMIC. ADR 0005. */
  pp?: Profile;
  /**
   * Effet explicite sur les prix du panier (Δ % de prix, niveau après montée en charge). S'il est présent, il remplace les canaux
   * dérivés de la catégorie de la mesure (revenu, transmission des impôts de production) — ADR 0005.
   */
  prices?: Partial<Record<BasketItem, Profile>>;
}

/** Postes du panier affichés au joueur (« effet sur le prix du pain / du plein / du loyer / de l'énergie »). */
export type BasketItem = "bread" | "fuel" | "rent" | "energy";
export const BASKET_ITEMS: readonly BasketItem[] = ["bread", "fuel", "rent", "energy"];

/**
 * Choc macroéconomique exogène d'un événement (ADR 0005), profil d'événement (scalaire = l'année suivant l'événement,
 * tableau = années successives), **niveau** de l'année sans persistance : `g` = écart de croissance réelle (pt, choc de
 * demande : Okun et recettes conjoncturelles jouent, le potentiel ne bouge pas), `infl` = inflation (pt), `oat` = OAT 10 ans (pt),
 * `trade` = balance commerciale (Md€).
 */
export interface Shock {
  g?: Profile;
  infl?: Profile;
  oat?: Profile;
  trade?: Profile;
}

/** Nature de la dépense d'une mesure, pour le choix du multiplicateur (PRD §3.1). */
export type SpendType = "investment" | "transfer" | "payroll";

/**
 * À quoi s'applique le curseur d'hypothèse `hyp` d'une mesure (ADR 0004) :
 * `cost_leak` → coût × (1 − hyp/100) ; `labour_decline` → population active −hyp %/an ;
 * `debt_share` → annulation de hyp % du stock (mesure `once`).
 */
export type HypTarget = "cost_leak" | "labour_decline" | "debt_share";

export interface Measure {
  id: string;
  label: string;
  /** Une ou deux phrases factuelles sur le mécanisme. */
  description?: string;
  category: MeasureCategory;
  /** Coût net en régime (Md€/an, >0 = dépense, <0 = recette) ou profil daté. */
  cost: Profile;
  cost_low?: Profile;
  cost_high?: Profile;
  /** Années de montée en charge. */
  ramp: number;
  /**
   * Coût politique saisi à la main (valeur du prototype). **Non lu par le moteur depuis la v0.3** (ADR 0009) : le coût politique
   * d'une mesure est calculé par la règle symétrique `political_cost = pc_per_bn(sens, incidence) × |Md€|`
   * (`measurePoliticalCost`). Conservé dans le contenu pour traçabilité.
   */
  political_cost: number;
  /**
   * Seule dérogation à la règle symétrique (ADR 0009) : coût politique annuel sourcé (sondage, étude d'acceptabilité). Ignoré si
   * `source` vaut "TODO".
   */
  political_cost_override?: { value: number; source: Source | "TODO"; note?: string };
  /**
   * Hypothèse contestée exposée au joueur (curseur). `applies_to` dit au moteur où brancher la valeur :
   * `cost_leak` → cost × (1 − hyp/100) ; `labour_decline` → population active −hyp %/an ;
   * `debt_share` → part de la dette annulée (mesure `once`). Absent = `cost_leak` (ADR 0004).
   */
  hyp?: {
    label: string;
    low: number;
    high: number;
    default: number;
    applies_to?: HypTarget;
    /**
     * `published` : bornes = chiffrages publiés le plus optimiste / pessimiste ; `estimated` : bornes proposées par le
     * chiffrage IA (mesure `estimé_ia`, non sourcées) ; sinon bornes de modélisation (ADR 0008 §b).
     */
    range?: "published" | "convention" | "estimated";
  };
  /** Nature de la dépense (multiplicateur). Absent → `investment` si la mesure porte un effet `fx.g`, sinon `transfer`. */
  spend_type?: SpendType;
  /**
   * Préréglage reproduisant des mesures déjà incluses dans la prévision de croissance de la trajectoire officielle (PLF 2027) :
   * pas d'impulsion de demande supplémentaire (la croissance de `baseline.outlook` la contient déjà). ADR 0014 §3.
   */
  demand_in_outlook?: boolean;
  fx: MeasureEffects;
  exclusive_with: string[];
  sources: Source[];
  claims: { supporters: string; critics: string };
  /**
   * `scaled` : chiffre dérivé d'une source par règle de trois ou contrefactuel documenté dans `note` (ADR 0004) ;
   * `proposal_envelope` : coût = enveloppe fixée par la proposition elle-même (statut `non_chiffré`).
   */
  derivation?: "scaled" | "proposal_envelope";
  /** Effet unique l'année d'adoption (ex. annulation de dette), sans montée en charge. */
  once?: boolean;
  /** Notes de modélisation (origine des profils datés, écarts entre chiffrages). */
  note?: string;
  status: MeasureStatus;
  /** Décomposition « concrètement » (PRD §14.4) : informative pour le moteur, affichée par l'UI. */
  breakdown?: MeasureBreakdownItem[];
  /** true : Σ `total_bn` = coût en régime ; false : décomposition illustrative. */
  breakdown_complete?: boolean;
  /** Variantes en unités physiques (seulement si la décomposition est complète). */
  variants?: MeasureVariant[];
}

/** Poste d'une décomposition de mesure (PRD §14.4). `total_bn` en Md€/an en régime ; absent = repère illustratif. */
export interface MeasureBreakdownItem {
  item: string;
  quantity?: number;
  unit?: string;
  unit_cost_eur?: number;
  total_bn?: number;
  delay_years?: number;
  source: Source | "TODO";
  note?: string;
}

/** Variante en unités physiques d'une mesure : coût recalculé depuis les coûts unitaires ; `scale` = quantité / quantité de référence. */
export interface MeasureVariant {
  id: string;
  label: string;
  quantity: number;
  unit: string;
  scale: number;
  cost: Profile;
  cost_low?: Profile;
  cost_high?: Profile;
  note?: string;
}

export interface EventOption {
  id: string;
  label: string;
  cost: Profile;
  /**
   * Coût politique **déclaré** hors argent, payé au choix (ex. effet institutionnel d'une dissolution). Depuis la v0.3 (ADR 0009)
   * le coût politique lié aux montants suit la règle symétrique (`pc_per_bn` × |Md€|, année par année) ; ce champ vaut 0 sauf
   * effet non monétaire déclaré.
   */
  political_cost: number;
  /**
   * Part du montant de l'option qui parvient au revenu des ménages (0–1, défaut 0 : services, entreprises, aide extérieure) :
   * même canal que `pp_transfer_share_<catégorie>` des mesures, dans les deux sens (chèque comme gel). ADR 0009 §3.
   */
  pp_share?: number;
  fx: MeasureEffects;
  /** Choc macro propre à l'option, ajouté au choc commun de l'événement (une option peut compenser une partie du choc). */
  shock?: Shock;
  /**
   * Issue défavorable possible (tirage avec le générateur seedé au moment du choix) : coût politique et effets ajoutés à ceux
   * de l'option. Ex. dissolution perdue.
   */
  risk?: { probability: number; label: string; political_cost?: number; fx?: MeasureEffects };
  /** Revenir sur l'annulation de dette : stock réintégré, dividende BdF rétabli, mesures d'annulation retirées (ADR 0005). */
  undo_debt_cancellation?: boolean;
  /** Effets non modélisables par `fx`, décrits pour le moteur. */
  note?: string;
}

export interface GameEvent {
  id: string;
  title: string;
  text: string;
  /** Déclencheur : scripté (année) ou conditionnel (expression sur l'état + probabilité). */
  trigger: { year?: number; condition?: string; probability?: number };
  options: EventOption[];
  /**
   * Option de référence (réponse minimale) : contrefactuel de l'attribution et choix par défaut sans réponse du joueur.
   * Obligatoire dans content/events.json (schéma) ; optionnelle dans le type pour les événements de test : à défaut, l'option la
   * moins coûteuse (Σ du profil), la première en cas d'égalité. ADR 0009 §4.
   */
  reference_option_id?: string;
  /** Choc macro commun à toutes les options (ex. récession : croissance −1,5 pt l'année suivante), ADR 0005. */
  shock?: Shock;
  sources: Source[];
  note?: string;
}

/** Entrées du joueur. */
export interface Programme {
  measure_ids: string[];
  /** Valeur retenue pour chaque curseur d'hypothèse. */
  hyp: Record<string, number>;
  /** Variante retenue (mesure → id de variante en unités physiques, `measure.variants`), ADR 0005 §13. */
  variants?: Record<string, string>;
}

export interface Financing {
  /** Hausses de recettes retenues (id → Md€ en régime). */
  revenue_raises: Record<string, number>;
  /** Rabot transversal sur les missions (part, 0–1). */
  across_the_board_cut: number;
  /**
   * Économies ciblées par groupe de missions (id de groupe de service de la baseline → % des crédits tendanciels du groupe, borné
   * à ±`cut_<groupe>_max_pct` ; négatif = hausse des crédits, symétrique : même règle de coût politique, indice de service
   * relevé, revenu des ménages).
   * Leviers d'économies symétriques des leviers de recettes (ADR 0009) : fuite, frein à l'activité, coût politique par la même règle,
   * dégradation de l'indice de service de la mission (niveau), effet sur le revenu des ménages (`pp_transfer_share_mission_<id>`).
   */
  mission_cuts?: Record<string, number>;
}

/** Choix du joueur aux événements : id événement → id option. */
export type Choices = Record<string, string>;

export interface LedgerLine {
  id: string;
  label: string;
  amount: number;
}

export interface YearAccount {
  resources: LedgerLine[];
  expenses: LedgerLine[];
  balance_state: number;
  balance_asso: number;
  balance_apul: number;
  balance_apu: number;
}

export interface YearState {
  year: number;
  kpis: Record<string, number>;
  service_index: Record<string, number>;
  account: YearAccount;
  events_fired: string[];
  journal: string[];
  /** Option retenue pour chaque événement déclenché cette année (choix du joueur ou option de référence par défaut). */
  choices_applied?: Record<string, string>;
  /** Issue du risque des options retenues cette année (événement → vrai si l'issue défavorable est tirée). */
  risks_hit?: Record<string, boolean>;
}

/**
 * Verdict de fin de mandat (calculé seulement si le mandat va à son terme).
 * @deprecated ADR 0015 : remplacé par le bilan chiffré comparé (`Scorecard`). Gardé pour la compatibilité.
 */
export interface Verdict {
  reelected: boolean;
  score: number;
  /** Somme des pondérations `score_w_*` (score maximal théorique). */
  score_max: number;
  /**
   * Conditions de réélection non remplies (vide si réélu) : capital politique, cohésion, confiance des marchés, déficit final
   * au-delà de `verdict_deficit_max_pct` (ADR 0005).
   */
  blocked_by: VerdictRuleId[];
  /** Règles explicites du verdict (ADR 0009 §5), toutes paramétrées : ce que l'UI affiche pour dire pourquoi. */
  rules: VerdictRule[];
}

export type VerdictRuleId = "deficit" | "majority" | "purchasing_power" | "confidence" | "capital" | "cohesion";

export interface VerdictRule {
  id: VerdictRuleId;
  /** Libellé en clair, seuil compris (gabarit rempli par les paramètres). */
  label: string;
  /** Valeur de fin de mandat. */
  value: number;
  /** Seuil (paramètre lu dans `param`). */
  threshold: number;
  /** Sens de la comparaison : `min` = value ≥ seuil ; `min_strict` = value > seuil (déficit : solde signé ≥ −seuil). */
  comparator: "min" | "min_strict";
  /** Identifiant du paramètre de seuil (pour l'écran « Hypothèses »). */
  param: string;
  ok: boolean;
  /** Unité d'affichage de `value` / `threshold` (vocabulaire `Unit`). */
  unit?: Unit;
  /** Nature de la règle : `economy` (finances publiques, marchés), `politics` (majorité, capital), `society` (pouvoir d'achat, cohésion). */
  kind?: "economy" | "politics" | "society";
  /** Voie alternative de la règle (déficit : amélioration sur le mandat). */
  alt?: { label: string; value: number; threshold: number; param: string; ok: boolean };
}

export interface Timeline {
  base_year: number;
  /** Point de départ (fin de l'année de base, ex. 2026 exécuté selon `baseline.outlook`) : KPIs principaux (ADR 0009 §6). */
  start?: Record<string, number>;
  years: YearState[];
  game_over: null | { year: number; reason: string };
  /** @deprecated ADR 0015 : « réélu / battu » ne juge plus la fin de mandat ; voir `scorecard`. */
  verdict?: Verdict;
}

/** Catalogue nécessaire au moteur pour résoudre `programme.measure_ids` et déclencher les événements. */
export interface SimContent {
  measures: Measure[];
  events: GameEvent[];
}

/**
 * API du moteur (PRD §6). Le 7e argument (catalogue mesures + événements) est optionnel pour rester compatible
 * avec la signature à 6 arguments ; sans catalogue, `programme.measure_ids` doit être vide et aucun événement ne se déclenche.
 */
export type Simulate = (
  baseline: Baseline,
  params: ParamSet,
  programme: Programme,
  financing: Financing,
  choices: Choices,
  seed: number,
  content?: SimContent,
  options?: SimOptions,
) => Timeline;

/** Événement figé (attribution, ADR 0009 §4) : déclenché à la fin de l'année `turn` (1–5) avec l'option et l'issue du risque données. */
export interface ScheduledEvent {
  turn: number;
  event_id: string;
  option_id: string;
  risk_hit: boolean;
}

export interface SimOptions {
  /** Séquence d'événements imposée (conditions et probabilités ignorées) : rejoue exactement les événements d'une partie. */
  schedule?: ScheduledEvent[];
  /**
   * Mode réaliste « sans mécaniques politiques non sourcées » (ADR 0015) : pas de vote du budget (censure), pas de crise de dette
   * déclenchée par l'indice de confiance, pas de frein de croissance par cet indice. Capital, majorité, cohésion et confiance
   * restent calculés (affichage, déclencheurs d'événements) mais n'interrompent plus le mandat. Défaut : false (comportement v0.3).
   */
  realistic?: boolean;
}

/* ------------------------------------------------------------------ bilan chiffré comparé (ADR 0015) */

/** Définition d'un indicateur du bilan (`content/scorecard.json`). Aucun chiffre du jeu : précision d'affichage seulement. */
export interface ScorecardIndicatorDef {
  id: string;
  /** Regroupement d'affichage (`finances`, `economie`, `services`). */
  group?: string;
  label: string;
  unit: string;
  /** Clé de `YearState.kpis`. */
  kpi: string;
  /** −1 pour afficher l'opposé de la clé (déficit = −solde). Défaut +1. */
  sign?: 1 | -1;
  /** Rapporter la clé au PIB de l'année (% du PIB). */
  per_gdp?: boolean;
  /** false : affiché sans entrer dans le décompte (même grandeur qu'un autre indicateur, dans une autre unité). Défaut true. */
  counted?: boolean;
  /** Compté seulement si la mécanique est entièrement sourcée (`mecanique_sourcee`, ADR 0016) : évolue seul avec `params`. */
  count_requires_sourced?: boolean;
  /** Raison affichée quand `counted: false`. */
  uncounted_reason?: string;
  /** `final` : valeur de la dernière année ; `mean` : moyenne des années du mandat. */
  aggregate: "final" | "mean";
  /** Sens de lecture propre à l'indicateur (« plus bas = mieux » pour la dette). */
  better_is: "lower" | "higher";
  /** Décimales affichées ; seuil d'égalité = la moitié du dernier chiffre affiché. */
  decimals: number;
  /** Définition officielle (ou « indice du jeu » si l'indicateur n'est pas une statistique publiée). */
  definition: string;
  /** Chiffre de départ dans la baseline (`bloc.clé`), pour la source et le badge de fact-check. */
  start_figure?: string;
  /** Principaux paramètres de la mécanique (motif `préfixe*` autorisé) : statut de sourçage de l'indicateur. */
  mechanism_params: string[];
}

export interface ScorecardDefs {
  /** Préréglage de référence « budget du gouvernement » (`content/presets.json`). */
  reference_preset_id: string;
  /** Bornes des curseurs de priorités du joueur (réglage d'interface, égal par défaut). */
  weights: { min: number; max: number; step: number; default: number; note?: string };
  indicators: ScorecardIndicatorDef[];
}

/** Programme préréglé tel que lu par le moteur (sous-ensemble de `content/presets.json`). */
export interface PresetProgramme {
  id: string;
  label: string;
  measure_ids: string[];
  financing: Financing;
}

export interface ScorecardContent extends SimContent {
  scorecard: ScorecardDefs;
  presets: PresetProgramme[];
}

/** Statut de sourçage de la mécanique d'un indicateur, calculé depuis les statuts de ses paramètres. */
export type MechanismStatus = "mecanique_sourcee" | "mecanique_partielle" | "mecanique_non_sourcee";
/** Comparaison selon le sens propre de l'indicateur ; `na` = valeur indisponible (mandat interrompu avant la première année). */
export type ScorecardComparison = "better" | "worse" | "equal" | "na";

export interface ScorecardIndicator {
  id: string;
  label: string;
  unit: string;
  group?: string;
  definition: string;
  decimals: number;
  better_is: "lower" | "higher";
  /** Compté dans le décompte (`summary`) ; false = même grandeur qu'un autre indicateur, autre unité. */
  counted: boolean;
  /** Pourquoi l'indicateur n'est pas compté (affichage) ; absent s'il est compté. */
  uncounted_reason?: string;
  /** Valeur au départ du mandat (fin de l'année de base) ; null si inconnue. */
  start: number | null;
  player: number | null;
  trend: number | null;
  plf: number | null;
  /** Écarts bruts (joueur − référence), dans l'unité de l'indicateur. */
  delta_vs_trend: number | null;
  delta_vs_plf: number | null;
  vs_trend: ScorecardComparison;
  vs_plf: ScorecardComparison;
  source_status: MechanismStatus;
  mechanism: { total: number; sourced: number; two_sources: number; unsourced: string[] };
}

export interface ScorecardCount {
  better: number;
  worse: number;
  equal: number;
  /** Indicateurs comparables (hors `na`). */
  total: number;
}

export interface Scorecard {
  /** Année comparée (dernière année simulée du joueur ; fin 2031 pour un mandat complet). */
  year: number;
  start_year: number;
  realistic: boolean;
  reference: { id: string; label: string };
  indicators: ScorecardIndicator[];
  player: Timeline;
  counterfactuals: { trend: Timeline; plf: Timeline };
  /** Interruptions (conventions de jeu, hors mode réaliste) : raison, ou null. */
  interrupted: { player: Timeline["game_over"]; trend: Timeline["game_over"]; plf: Timeline["game_over"] };
  /**
   * Décompte neutre, sans pondération : « meilleur que le tendanciel sur X indicateurs sur Y ». `sourced_*` : hors indicateurs
   * dont la mécanique est entièrement non sourcée.
   */
  summary: {
    vs_trend: ScorecardCount;
    vs_plf: ScorecardCount;
    sourced_vs_trend: ScorecardCount;
    sourced_vs_plf: ScorecardCount;
    /** Même décompte par groupe d'indicateurs (finances, économie, services) : un domaine riche en indicateurs ne domine pas en silence. */
    by_group: Record<string, { vs_trend: ScorecardCount; vs_plf: ScorecardCount }>;
  };
}

/** Une contribution à l'écart d'un indicateur (« Pourquoi ? »). */
export interface GapItem {
  kind: "measure" | "event" | "financing" | "reference";
  id: string;
  label: string;
  delta: number;
}

export interface GapExplanation {
  gap: number | null;
  items: GapItem[];
  /** Écart − Σ contributions : interactions entre éléments (les contributions ne s'additionnent pas exactement). */
  residual: number | null;
  rationale: string;
}

export interface ScorecardExplanation {
  year: number;
  indicators: Array<{ id: string; vs_trend: GapExplanation; vs_plf: GapExplanation }>;
}
